"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CustomerHandle = void 0;
const dayjs_1 = __importDefault(require("dayjs"));
const BudgetHandle_1 = require("./BudgetHandle");
const BudgetsHandle_1 = require("./BudgetsHandle");
const ContractHoursType_1 = require("./ContractHoursType");
const CustomerBurnedBudget_1 = require("./CustomerBurnedBudget");
const CustomerBurnedBudgetHandle_1 = require("./CustomerBurnedBudgetHandle");
const CustomerWarningFlag_1 = require("./CustomerWarningFlag");
const EMPTY_BURNED_BURDGET = {
    budgeted: 0,
    unbudgeted: 0,
    combinations: {},
};
class CustomerHandle {
    constructor(customer) {
        this.customer = customer;
        this.burnedBudget = {};
        this.warnings = undefined;
        Object.assign(this, customer);
        this.budget = JSON.parse(JSON.stringify(customer.budget || []));
        this.budgetsHandle = new BudgetsHandle_1.BudgetsHandle(this.budget, customer.contractHourTypes || ContractHoursType_1.DEFAULT_CONTRACT_HOURS);
        this.burnedBudget = customer.burnedBudget || {};
        this.contractHourTypes = [
            ...(customer.contractHourTypes || ContractHoursType_1.DEFAULT_CONTRACT_HOURS),
        ];
        this.flags = [...(customer.flags || [])];
        this.gir = customer.gir
            ? JSON.parse(JSON.stringify(customer.gir))
            : undefined;
        this.kickoffDateTime = customer.kickoffDate
            ? new Date(customer.kickoffDate?.year || 0, customer.kickoffDate?.month || 0, customer.kickoffDate?.day || 0)
            : undefined;
        this.productTypes = [...(customer.productTypes || [])];
        this.skills = [...(customer.skills || [])];
        this.jira = {
            customerCommunication: customer.jira?.customerCommunication || false,
            qbr: customer.jira?.qbr || false,
            updatedOutOfCP: customer.jira?.updatedOutOfCP || false,
        };
    }
    getKickoffDate() {
        return this.kickoffDateTime;
    }
    getOffboardingDate() {
        return typeof this.offboardingDate?.year === "number" &&
            typeof this.offboardingDate?.month === "number" &&
            typeof this.offboardingDate?.day === "number"
            ? new Date(this.offboardingDate.year, this.offboardingDate.month, this.offboardingDate?.day)
            : undefined;
    }
    isOffboarded(now = new Date()) {
        const offboarding = this.getOffboardingDate();
        return !!offboarding && offboarding.getTime() <= now.getTime();
    }
    getBurnedBudgetCombinationKeys(year, month) {
        return this.getBurnedBudget(year, month).getCombinationKeys();
    }
    getBurnedBudget(year, month) {
        const key = (0, CustomerBurnedBudget_1.serializeCustomerBurnedBudgetKey)(year, month);
        const burnedBudget = this.burnedBudget[key] || EMPTY_BURNED_BURDGET;
        return new CustomerBurnedBudgetHandle_1.CustomerBurnedBudgetHandle(burnedBudget);
    }
    getBudget(year, month) {
        const now = new Date(year, month);
        if (this.isOffboarded(now)) {
            return new BudgetHandle_1.BudgetHandle((0, BudgetsHandle_1.createEmptyBudget)(""), this.getContractHourTypes());
        }
        return this.budgetsHandle.getBudget(year, month);
    }
    getTeam(year = new Date().getFullYear(), month = new Date().getMonth()) {
        return this.getBudget(year, month).team;
    }
    isInTeam(year, month, teamId) {
        return this.getTeam(year, month) === teamId;
    }
    hasTeam(year, month) {
        return !!this.getTeam(year, month);
    }
    hasBudget(year, month) {
        const budget = this.getBudget(year, month);
        return budget.emHours + budget.opsHours + budget.teamHours > 0;
    }
    wasEverInTeam(teamId) {
        const budgets = this.budget || [];
        return budgets.findIndex((budget) => budget.team === teamId) >= 0;
    }
    hasBudgetAndNoTeam(year, month) {
        return this.hasBudget(year, month) && !this.getBudget(year, month).team;
    }
    hasNoBudgetAfterKickoffDate(year, month) {
        if (!this.kickoffDate || !this.kickoffDateTime) {
            return false;
        }
        const givenDateTime = new Date(year, month + 1, 0);
        if (givenDateTime.getTime() < this.kickoffDateTime.getTime()) {
            return false;
        }
        if (this.hasBudget(year, month)) {
            return false;
        }
        return !this.hasBudget(this.kickoffDate.year, this.kickoffDate.month);
    }
    hasNoEMFlag(year, month) {
        return (!this.engagementManager &&
            (!!this.kickoffDate || this.hasBudget(year, month + 1)));
    }
    getWarnings(year, month) {
        if (!this.warnings || !(this.warnings instanceof Set)) {
            const flags = new Set();
            if (this.hasBudgetAndNoTeam(year, month)) {
                flags.add(CustomerWarningFlag_1.CustomerWarningFlag.HAS_BUDGET_AND_NO_TEAM);
            }
            if (!this.harvestProjectId) {
                flags.add(CustomerWarningFlag_1.CustomerWarningFlag.HARVEST_IS_UNDEFINED);
            }
            if (!this.jiraProjectKey) {
                flags.add(CustomerWarningFlag_1.CustomerWarningFlag.JIRA_KEY_UNDEFINED);
            }
            if (this.hasNoEMFlag(year, month)) {
                flags.add(CustomerWarningFlag_1.CustomerWarningFlag.EM_IS_UNDEFINED);
            }
            if (this.hasNoBudgetAfterKickoffDate(year, month)) {
                flags.add(CustomerWarningFlag_1.CustomerWarningFlag.HAS_NO_BUDGET_ON_KICKOFF_DATE);
            }
            this.warnings = flags;
        }
        return this.warnings;
    }
    getContractHourTypes() {
        return this.contractHourTypes || ContractHoursType_1.DEFAULT_CONTRACT_HOURS;
    }
    getBurnedBudgetOverPlannedHours(year, month) {
        if (!this.hasBudget(year, month))
            return 0;
        const budget = this.getBudget(year, month);
        const burned = this.getBurnedBudget(year, month);
        const planned = budget.getPlannedHours();
        return planned > 0 ? burned.getTotal() / planned : 0;
    }
    getNewBudgetDate() {
        return this.budgetsHandle.getNewBudgetDate();
    }
    compare(other) {
        return this.name.localeCompare(other.name);
    }
    getEngagementLengthInMs(now = new Date()) {
        return this.getKickoffDate()
            ? now.getTime() - (this.getKickoffDate()?.getTime() || 0)
            : undefined;
    }
    isQBRFirableToday(now = new Date()) {
        const RATE_IN_DAYS = 90;
        const qbrStartDate = this.qbrStartDate;
        if (!qbrStartDate) {
            return false;
        }
        const days = (0, dayjs_1.default)(now)
            .startOf("day")
            .diff((0, dayjs_1.default)(qbrStartDate).startOf("day"), "day", true);
        return days > 0 && days % RATE_IN_DAYS === 0;
    }
}
exports.CustomerHandle = CustomerHandle;
//# sourceMappingURL=CustomerHandle.js.map